import React, { useMemo, useState } from 'react';
import {
  Trophy, DollarSign,
  ArrowUpRight, ArrowDownRight, Package, Gauge, Info
} from 'lucide-react';
import { useSalesRepLeaderboard, useProductVelocity } from '../../hooks/useAdminDashboard';

// Admin "Sales Reps" leaderboard. Ranks every salesRepEnabled distributor
// against each other and against the MEDIAN AM (the full active-distributor
// cohort). All money figures are realized (paid invoices); a rep's margin is
// their frozenPricing.distributorMarkup commission. Best-seller / high-velocity
// products render system-wide or scoped to a single rep's book of business.

type Preset = '30d' | '90d' | 'ytd' | 'all';

const presetToRange = (preset: Preset): { startDate?: Date; endDate?: Date } | undefined => {
  if (preset === 'all') return undefined;
  const end = new Date();
  if (preset === 'ytd') return { startDate: new Date(end.getFullYear(), 0, 1), endDate: end };
  const days = preset === '30d' ? 30 : 90;
  const start = new Date(end);
  start.setDate(start.getDate() - (days - 1));
  start.setHours(0, 0, 0, 0);
  return { startDate: start, endDate: end };
};

const fmtMoney = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);
const fmtNum = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : (Number.isInteger(n) ? n.toString() : n.toFixed(2));
const fmtPct = (n: number) => `${(n || 0).toFixed(1)}%`;

interface Rep {
  rank: number;
  distributorId: string;
  name: string;
  revenue: number;
  margin: number;
  orderCount: number;
  avgOrderValue: number;
  reorderInterval: number | null;
  activeClinics: number;
  totalClinics: number;
  newClinics: number;
  activePct: number;
  deltaVsMedian: Record<string, number>;
  percentile: Record<string, number>;
}

type SortKey = 'rank' | 'margin' | 'revenue' | 'orderCount' | 'avgOrderValue' | 'activePct' | 'totalClinics' | 'newClinics';

// A numeric cell that, when a median value is provided, shows a small colored
// delta chip (rep value minus the median AM) under the figure.
const DeltaCell: React.FC<{
  value: string;
  delta?: number;
  fmtDelta?: (n: number) => string;
}> = ({ value, delta, fmtDelta = fmtMoney }) => {
  const showDelta = delta !== undefined && Math.abs(delta) >= 0.005;
  const positive = (delta || 0) >= 0;
  return (
    <div className="flex flex-col items-end">
      <span className="font-medium text-gray-900">{value}</span>
      {showDelta && (
        <span className={`inline-flex items-center gap-0.5 text-xs ${positive ? 'text-green-600' : 'text-red-500'}`}>
          {positive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          {fmtDelta(Math.abs(delta!))}
        </span>
      )}
    </div>
  );
};

const SalesRepLeaderboard: React.FC = () => {
  const [preset, setPreset] = useState<Preset>('90d');
  const [sortKey, setSortKey] = useState<SortKey>('margin');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [productRepId, setProductRepId] = useState<string>(''); // '' = system-wide

  const dateRange = useMemo(() => presetToRange(preset), [preset]);
  const lbQuery = useSalesRepLeaderboard(dateRange);
  const pvQuery = useProductVelocity(dateRange, productRepId || undefined);

  const reps: Rep[] = lbQuery.data?.reps || [];
  const medianAm: Record<string, number> = lbQuery.data?.medianAm || {};
  const cohortSize: number = lbQuery.data?.cohortSize || 0;

  const sortedReps = useMemo(() => {
    const arr = [...reps];
    arr.sort((a, b) => {
      const av = (a as any)[sortKey] ?? 0;
      const bv = (b as any)[sortKey] ?? 0;
      const cmp = sortKey === 'rank' ? av - bv : bv - av;
      return sortDir === 'asc' ? -cmp : cmp;
    });
    return arr;
  }, [reps, sortKey, sortDir]);

  const products = pvQuery.data?.products || [];
  const windowDays: number = pvQuery.data?.windowDays || 0;
  const topByVelocity = useMemo(
    () => [...products].sort((a: any, b: any) => b.velocity - a.velocity).slice(0, 10),
    [products]
  );
  const topByRevenue = products.slice(0, 10);

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const SortHeader: React.FC<{ k: SortKey; label: string; className?: string }> = ({ k, label, className = '' }) => (
    <th
      className={`px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide cursor-pointer select-none hover:text-gray-800 ${className}`}
      onClick={() => toggleSort(k)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortKey === k && <span className="text-gray-400">{sortDir === 'desc' ? '▼' : '▲'}</span>}
      </span>
    </th>
  );

  const presets: { id: Preset; label: string }[] = [
    { id: '30d', label: '30d' },
    { id: '90d', label: '90d' },
    { id: 'ytd', label: 'YTD' },
    { id: 'all', label: 'All time' }
  ];

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-amber-500" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Sales Rep Performance</h3>
            <p className="text-xs text-gray-500">
              {lbQuery.isLoading ? 'Loading…' : `${reps.length} sales rep${reps.length === 1 ? '' : 's'} · median over ${cohortSize} active AM${cohortSize === 1 ? '' : 's'}`}
            </p>
          </div>
        </div>
        <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
          {presets.map(p => (
            <button
              key={p.id}
              onClick={() => setPreset(p.id)}
              className={`px-3 py-1.5 text-sm transition-colors ${preset === p.id ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Leaderboard table */}
      <div className="bg-white shadow-sm rounded-2xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2 text-sm text-gray-600">
          <Info className="w-4 h-4 text-gray-400" />
          Realized (paid-invoice) figures. Green/red chips are each rep’s delta vs. the median AM.
        </div>
        {lbQuery.isError ? (
          <div className="p-6 text-sm text-red-600">{(lbQuery.error as Error)?.message || 'Failed to load leaderboard'}</div>
        ) : lbQuery.isLoading ? (
          <div className="p-6 space-y-2">
            {[...Array(5)].map((_, i) => <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />)}
          </div>
        ) : reps.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">No sales reps found. Enable “sales rep” mode on an account manager to see them ranked here.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <SortHeader k="rank" label="#" className="text-left" />
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Sales Rep</th>
                  <SortHeader k="margin" label="Margin" className="text-right" />
                  <SortHeader k="revenue" label="Revenue" className="text-right" />
                  <SortHeader k="orderCount" label="Orders" className="text-right" />
                  <SortHeader k="avgOrderValue" label="Avg Order" className="text-right" />
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Reorder</th>
                  <SortHeader k="activePct" label="Active %" className="text-right" />
                  <SortHeader k="totalClinics" label="Clinics" className="text-right" />
                  <SortHeader k="newClinics" label="New" className="text-right" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {/* Pinned median-AM reference row */}
                <tr className="bg-amber-50/60">
                  <td className="px-3 py-2 text-xs text-amber-700 font-semibold">—</td>
                  <td className="px-3 py-2 text-sm font-semibold text-amber-800">Median AM (baseline)</td>
                  <td className="px-3 py-2 text-right text-sm text-amber-800">{fmtMoney(medianAm.margin)}</td>
                  <td className="px-3 py-2 text-right text-sm text-amber-800">{fmtMoney(medianAm.revenue)}</td>
                  <td className="px-3 py-2 text-right text-sm text-amber-800">{fmtNum(medianAm.orderCount)}</td>
                  <td className="px-3 py-2 text-right text-sm text-amber-800">{fmtMoney(medianAm.avgOrderValue)}</td>
                  <td className="px-3 py-2 text-right text-sm text-amber-800">—</td>
                  <td className="px-3 py-2 text-right text-sm text-amber-800">{fmtPct(medianAm.activePct)}</td>
                  <td className="px-3 py-2 text-right text-sm text-amber-800">{fmtNum(medianAm.totalClinics)}</td>
                  <td className="px-3 py-2 text-right text-sm text-amber-800">{fmtNum(medianAm.newClinics)}</td>
                </tr>
                {sortedReps.map(rep => (
                  <tr key={rep.distributorId} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-sm font-semibold text-gray-500">{rep.rank}</td>
                    <td className="px-3 py-2 text-sm font-medium text-gray-900">{rep.name}</td>
                    <td className="px-3 py-2 text-right text-sm"><DeltaCell value={fmtMoney(rep.margin)} delta={rep.deltaVsMedian?.margin} /></td>
                    <td className="px-3 py-2 text-right text-sm"><DeltaCell value={fmtMoney(rep.revenue)} delta={rep.deltaVsMedian?.revenue} /></td>
                    <td className="px-3 py-2 text-right text-sm"><DeltaCell value={fmtNum(rep.orderCount)} delta={rep.deltaVsMedian?.orderCount} fmtDelta={fmtNum} /></td>
                    <td className="px-3 py-2 text-right text-sm"><DeltaCell value={fmtMoney(rep.avgOrderValue)} delta={rep.deltaVsMedian?.avgOrderValue} /></td>
                    <td className="px-3 py-2 text-right text-sm text-gray-700">{rep.reorderInterval === null ? '—' : `${rep.reorderInterval}d`}</td>
                    <td className="px-3 py-2 text-right text-sm"><DeltaCell value={fmtPct(rep.activePct)} delta={rep.deltaVsMedian?.activePct} fmtDelta={(n) => `${n.toFixed(1)}pt`} /></td>
                    <td className="px-3 py-2 text-right text-sm text-gray-700">{rep.activeClinics}/{rep.totalClinics}</td>
                    <td className="px-3 py-2 text-right text-sm"><DeltaCell value={fmtNum(rep.newClinics)} delta={rep.deltaVsMedian?.newClinics} fmtDelta={fmtNum} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Products: best-sellers + high-velocity */}
      <div className="bg-white shadow-sm rounded-2xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-indigo-500" />
            <h3 className="text-lg font-semibold text-gray-900">Top Products</h3>
            {windowDays > 0 && <span className="text-xs text-gray-400">velocity over {windowDays}d</span>}
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Scope:</label>
            <select
              value={productRepId}
              onChange={(e) => setProductRepId(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All reps (system-wide)</option>
              {reps.map(r => <option key={r.distributorId} value={r.distributorId}>{r.name}</option>)}
            </select>
          </div>
        </div>

        {pvQuery.isLoading ? (
          <div className="p-6 space-y-2">
            {[...Array(4)].map((_, i) => <div key={i} className="h-8 bg-gray-100 rounded animate-pulse" />)}
          </div>
        ) : products.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">No product sales in this window.</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-gray-100">
            <ProductTable
              title="Best Sellers"
              subtitle="by revenue"
              icon={<DollarSign className="w-4 h-4 text-green-500" />}
              rows={topByRevenue}
              primary="revenue"
            />
            <ProductTable
              title="Highest Velocity"
              subtitle="units / day"
              icon={<Gauge className="w-4 h-4 text-orange-500" />}
              rows={topByVelocity}
              primary="velocity"
            />
          </div>
        )}
      </div>
    </div>
  );
};

const ProductTable: React.FC<{
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  rows: any[];
  primary: 'revenue' | 'velocity';
}> = ({ title, subtitle, icon, rows, primary }) => (
  <div className="p-4">
    <div className="flex items-center gap-2 mb-3">
      {icon}
      <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
      <span className="text-xs text-gray-400">{subtitle}</span>
    </div>
    <table className="w-full">
      <thead>
        <tr className="text-xs text-gray-400 uppercase tracking-wide">
          <th className="text-left font-medium py-1">Product</th>
          <th className="text-right font-medium py-1">Units</th>
          <th className="text-right font-medium py-1">{primary === 'revenue' ? 'Revenue' : 'Vel.'}</th>
          <th className="text-right font-medium py-1">Clinics</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-50">
        {rows.map((p, i) => (
          <tr key={p.productId || p.name || i} className="text-sm">
            <td className="py-1.5 pr-2 text-gray-900 truncate max-w-[180px]" title={p.name}>{p.name}</td>
            <td className="py-1.5 text-right text-gray-700">{p.units}</td>
            <td className="py-1.5 text-right font-medium text-gray-900">
              {primary === 'revenue' ? fmtMoney(p.revenue) : `${fmtNum(p.velocity)}/d`}
            </td>
            <td className="py-1.5 text-right text-gray-500">{p.clinicCount}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default SalesRepLeaderboard;
